import React from 'react';
import Spinner from '@developers/ui/Spinner';
import slugify from '../../utils/slugify';
import Header from '../../components/BrandHeader';
import Filter from '../../components/BrandEquipmentFilter';
import Lists from '../../components/BrandEquipmentLists';
import capiltalize from '@developers/utils/capitalize';
import * as brandsAjax from '../../ajax/brands';
import Link from 'next/link';
import Head from 'next/head'
import { useRouter } from 'next/router';
import { useAjax } from '@developers/use-ajax';
import type { Brand } from '../../types/Brand';
import type { Manufacturer } from '@developers/types/FRP/Manufacturer';

const basePath = '/brand/';

export default function BrandPage() {
  let router = useRouter();
  const [search, changeSearch] = React.useState('');
  const [brand, getBrand, getBrandMeta] = useAjax(brandsAjax.getBrand, {} as Brand, true);
  const [manufacturer, getManufacturerByBrand, getManufacturerByBrandMeta] = useAjax(brandsAjax.getManufacturerByBrand, {} as Manufacturer, true);
  const [equipment, getEquipment, getEquipmentMeta] = useAjax(brandsAjax.getEquipmentByBrand, [], true);

  const id = router.query.params?.[0];
  const params = {
    type: router.query.params?.[1],
    subtype: router.query.params?.[2],
    markettype: router.query.params?.[3]
  }

  /**
   * Get all brand data for this page
   */
  React.useEffect(() => {
    if (id === 'View') {
      // Support older urls by navigating to current one.
      // type is actually the brand id (/brand/View/{id}/{name})
      router.push('/brand/' + params.type);
    } else if (id) {
      getBrand(id);
      getManufacturerByBrand(id);
      getEquipment(id);
    }
  }, [id]);

  /**
   * Reset type selects if searching for specific term
   */
  React.useEffect(() => {
    if (search.length > 0 && typeof params.type !== 'undefined' && params.type) {
      // If filtering by at least type (maybe more) AND searching
      // Prioritize the search term and reset the type selects
      router.push(basePath + id);
    }
  }, [search]);

  const sortEquipmentAlphabetically = (a, b) => {
    let equipmentA = a.equipment_name.toUpperCase();
    let equipmentB = b.equipment_name.toUpperCase();

    let comparison = 0;
    if (equipmentA > equipmentB) {
      comparison = 1;
    } else if (equipmentA < equipmentB) {
      comparison = -1;
    }
    return comparison;
  }

  /**
   * Filter all data by either a search term OR the combo of types selected
   */
  const data = React.useMemo(() => {

    // Copy data 'equipment' and store in new variable so it does not get changed 
    // during the search filter
    let filtered = equipment.length > 0 ? equipment.map((type) => {
      return {...type, subtypes: type.subtypes.map((subtype) => {
        return {...subtype, market_types: subtype.market_types.map((market_type) => {
          return {...market_type, equipment: [...market_type.equipment].sort(sortEquipmentAlphabetically).map((equipment) => {
            return {...equipment};
          })}
        })}
      })}
    }) : [];

    // Filter by search term
    if (search.trim() != '') {
      // If typing search term ONLY filter by search
      const searchTerm = search.trim().toLowerCase();
      filtered = filtered.reduce((types, type) => {
        type.subtypes = type.subtypes.reduce((subtypes, subtype) => {
          subtype.market_types = subtype.market_types.reduce((marketTypes, marketType) => {
            marketType.equipment = marketType.equipment.reduce((equipment, e) => {
            const equipment_name = e.equipment_name.toLowerCase();
            const match = equipment_name.includes(searchTerm);

            if (match) {
              equipment.push(e);
              return equipment;
            } else {
              return equipment;
            }
            }, []);
            if (marketType.equipment.length > 0) {
              marketTypes.push(marketType);
              return marketTypes;
            } else {
              return marketTypes;
            }
          }, []);
          if (subtype.market_types.length > 0) {
            subtypes.push(subtype);
            return subtypes;
          } else {
            return subtypes;
          }
        }, []);

        if (type.subtypes.length > 0) {
          types.push(type);
          return types;
        } else {
          return types;
        }
      }, []);
    } else {
      // Check if filtering by type selects
      if (typeof params.type !== 'undefined' && params.type != '') {
        // Filter by type
        filtered = filtered.filter(type => {
        return params.type === slugify(type.equipment_type_name);
        });
      }

      if (typeof params.subtype !== 'undefined' && params.subtype != '') {
        // Filter by subtype
        filtered = filtered.map(type => {
          type.subtypes = type.subtypes.filter(subtype => {
            if (subtype.equipment_sub_type_name.includes('/')) {
              let subtypeName = subtype.equipment_sub_type_name.replace('/', '-').toLowerCase();
              return params.subtype === subtypeName;
            } else {
              return params.subtype === slugify(subtype.equipment_sub_type_name);
            }

          });
          return type;
        });
      }

      if (typeof params.markettype !== 'undefined' && params.markettype != '') {
        // Filter by market type
        filtered = filtered.map(type => {
          type.subtypes = type.subtypes.map(subtype => {
            subtype.market_types = subtype.market_types.filter(marketType => {
              return params.markettype === slugify(marketType.name)
            })
            return subtype;
          })
          return type;
        });
      }
    }

    return filtered;
  }, [equipment, search, id, params.type, params.subtype, params.markettype]);

  const handlePageChange = (path) => {
    if (path != '') {
      router.push(basePath + id + '/' + path);
    } else {
      router.push(basePath + id);
    }
  };

  if (!getBrandMeta.loading && !brand.id) {
    // Page has no brand information
    return <NoEquipmentFound />
  }

  // Whether or not we should expand all of the lists automatically
  const expanded = (typeof params.subtype !== 'undefined' && params.subtype != '') || search.length >= 2;

  return (
    <>
      <Head>
        <title>{brand.name} | Fitness and Exercise Equipment Repair Parts</title>
        <meta 
          name="description" 
          content={'Find spare or replacement parts for ' + brand.name + ' equipment and more. #1 in parts and service for exercise and fitness equipment.'}
        />
        <meta httpEquiv="content-language" content="en-us" />
      </Head>
      <Spinner show={getBrandMeta.loading} />
      <Header loading={getBrandMeta.loading} brand={brand} />
      <div style={{ minHeight: 80 }}>
        <Filter 
          loading={getEquipmentMeta.loading}
          data={equipment} 
          type={params.type} 
          subtype={params.subtype} 
          marketType={params.markettype} 
          changePage={handlePageChange}
          search={search}
          changeSearch={changeSearch}
        />
      </div>
      <Lists 
        loading={getEquipmentMeta.loading}
        types={data} 
        expanded={expanded} 
      />
      {!getManufacturerByBrandMeta.loading ?
        <p style={{ paddingBottom: 10 }}>{brand.name} is a registered trademark of {manufacturer.manufacturer_legal_name}</p>
      : null}
    </>
  );
};

function NoEquipmentFound() {

  let path = window.location.pathname;
  let brand_in_url = path.split('/').pop();
  let brandMessage = ''

  if (!Number.isInteger(parseInt(brand_in_url))) {
    brandMessage = 'for ' + brand_in_url.split('-').map(segment => capiltalize(segment)).join(' ');
  }

  return (
    <div style={{ textAlign: 'center' }} >
      <h1 style={{ padding: 20 }}>
        No equipment found {brandMessage}
      </h1>
      <Link href="/brand">View all brands</Link>
    </div>
  );
}